import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { Link } from 'react-router-dom';
import { Button } from 'semantic-ui-react';
import PageLoader from 'components/PageLoader';
import MainContent from 'components/MainContent';
import BackToTop from 'components/Measure/BackToTop';
import InfoBox from 'components/Measure/View/InfoBox';
import PageHeader from 'components/Measure/PageHeader';
import PageBackground from 'components/PageBackground';
import { Assessment } from 'types/api/envScales/Assessment';
import ClassroomExamples from 'components/Measure/ClassroomExamples';
import ObservationScores from 'components/Measure/ObservationScores';
import { useGetSettings } from 'actions/envScales/settings';
import AttachmentViewer from 'components/Measure/View/ExamplesAndNotes/Attachments/AttachmentViewer';
import { ENV_SCALES_URL } from 'constants/routes';
import ScoreSummary from 'components/Measure/View/ScoreSummary';
import CustomQuestions from 'components/Measure/CustomQuestions';
import { ObservationNotes } from 'components/Measure/ObservationNotes';

import {
  Section,
  SectionActions,
  ActionWrapper
} from 'components/Measure/View/Styled';

function getPDFLink(id: number) {
  return `${ENV_SCALES_URL}/api/v1/assessments/${id}.pdf`;
}

function getCSVLink(assessment: Assessment) {
  const environment = assessment.assessment_template.env_scales
    ? '?includes_environments=true'
    : '';
  return `/api/v2/measurement/assessments/${assessment.id}.csv${environment}`;
}

interface CompletedCLASSProps {
  /** CLASS Assessment. */
  assessment: Assessment;

  /** User has observer role in the current account. */
  isObserver: boolean;

  /** User has admin role in the current account. */
  isAdmin: boolean;

  /** User has supervisor role in the current account. */
  isSupervisor: boolean;
}

function CompletedCLASS({
  assessment,
  isObserver,
  isAdmin,
  isSupervisor
}: CompletedCLASSProps) {
  const { data: settings, isSuccess } = useGetSettings(assessment.account_guid);

  if (!isSuccess) {
    return <PageLoader />;
  }

  const {
    strength_and_growth_enabled: displayClassroomExamples,
    video_enabled: isVideoObservation
  } = settings;

  const header = assessment.informal
    ? i18n.ft(messages.informal, {
        template: assessment.assessment_template.name
      })
    : i18n.ft(messages.header, {
        template: assessment.assessment_template.name
      });

  return (
    <MainContent maxWidth={1280}>
      <div className="text-[#364A5E] mt-8 px-[0.75rem] sm:px-[1.5rem] lg:px-[4.5rem]">
        <PageHeader title={header} />

        <PageBackground>
          <InfoBox
            data={assessment}
            attachToBottom
            extended
            isAdmin={isAdmin}
            isSupervisor={isSupervisor}
            isVideoObservation={isVideoObservation}
            requiresApproval={assessment.requires_approval}
          />

          {assessment.includes_environments && (
            <div className="mt-8 sm:mt-10">
              <ScoreSummary
                assessmentId={assessment.id}
                infoData={assessment}
                isEditable={false}
              />
            </div>
          )}

          <ObservationScores fitted={assessment.includes_environments} />

          {settings.notes_enabled && (
            <ObservationNotes assessment={assessment} />
          )}

          {displayClassroomExamples && (
            <ClassroomExamples observation={assessment.observation} />
          )}
          <CustomQuestions />
          <AttachmentViewer />

          <BackToTop />

          <Section className="mt-2 rounded-b-xl">
            <SectionActions>
              <ActionWrapper>
                <Button
                  as={Link}
                  to={RouteHelpers.getPath('measure')}
                  fluid
                  basic
                  icon="arrow alternate circle left outline"
                  content={i18n.ft(messages.returnToList)}
                />
              </ActionWrapper>
              {!isObserver &&
                assessment.primary_observer &&
                assessment.status === 'completed' && (
                  <>
                    <ActionWrapper>
                      <Button
                        fluid
                        color="blue"
                        as="a"
                        href={getPDFLink(assessment.id)}
                        target="_blank"
                        content={i18n.ft(messages.downloadPDF)}
                      />
                    </ActionWrapper>

                    <ActionWrapper>
                      <Button
                        fluid
                        color="blue"
                        as="a"
                        href={getCSVLink(assessment)}
                        target="_blank"
                        content={i18n.ft(messages.downloadCSV)}
                      />
                    </ActionWrapper>
                  </>
                )}
            </SectionActions>
          </Section>
        </PageBackground>
      </div>
    </MainContent>
  );
}

export default CompletedCLASS;
